<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportSectionCard from '../ReportSectionCard.vue';
import { BALANCE_STATUS_KEYS, percentOf } from './aiUsagePresentation';

const props = defineProps({
  billing: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();

const isAvailable = computed(() => props.billing.status === 'available');

const formatCurrency = value =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: props.billing.currency || 'USD',
  });

const spentPercent = computed(() =>
  percentOf(props.billing.month_cost, props.billing.monthly_limit)
);

const statusKey = computed(
  () => BALANCE_STATUS_KEYS[props.billing.status] || 'UNAVAILABLE'
);

// Estados informativos usam tom neutro; falhas de acesso usam aviso âmbar.
const isWarning = computed(() =>
  ['PERMISSION_REQUIRED', 'UNAVAILABLE'].includes(statusKey.value)
);
</script>

<template>
  <ReportSectionCard
    :title="t('AI_USAGE_REPORT.BALANCE.TITLE')"
    :description="t('AI_USAGE_REPORT.BALANCE.DESCRIPTION')"
    icon="i-simple-icons-openai"
  >
    <div v-if="loading" class="grid gap-2" aria-busy="true">
      <span class="h-8 w-32 rounded bg-n-alpha-2 motion-safe:animate-pulse" />
      <span class="h-3 w-full rounded bg-n-alpha-2 motion-safe:animate-pulse" />
    </div>

    <div v-else-if="isAvailable" data-test-id="aiBalanceAvailable">
      <p class="mb-0 text-xs font-medium text-n-slate-11">
        {{ t('AI_USAGE_REPORT.BALANCE.REMAINING') }}
      </p>
      <p class="mb-3 text-2xl font-semibold tabular-nums text-n-slate-12">
        {{ formatCurrency(billing.remaining) }}
      </p>
      <div
        class="h-2 overflow-hidden rounded-full bg-n-slate-4"
        role="progressbar"
        :aria-valuenow="spentPercent"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="
          t('AI_USAGE_REPORT.BALANCE.SPENT', {
            spent: formatCurrency(billing.month_cost),
            limit: formatCurrency(billing.monthly_limit),
          })
        "
      >
        <div
          class="h-full rounded-full"
          :class="spentPercent >= 90 ? 'bg-n-ruby-9' : 'bg-n-brand'"
          :style="{ width: `${spentPercent}%` }"
        />
      </div>
      <p class="mb-0 mt-2 text-xs tabular-nums text-n-slate-11">
        {{
          t('AI_USAGE_REPORT.BALANCE.SPENT', {
            spent: formatCurrency(billing.month_cost),
            limit: formatCurrency(billing.monthly_limit),
          })
        }}
      </p>
    </div>

    <div
      v-else
      data-test-id="aiBalanceStatus"
      class="flex items-start gap-3 rounded-xl border p-3"
      :class="
        isWarning
          ? 'border-n-amber-6 bg-n-amber-2'
          : 'border-n-weak bg-n-solid-2 dark:border-n-strong'
      "
      role="status"
    >
      <span
        class="mt-0.5 size-4 shrink-0"
        :class="
          isWarning
            ? 'i-hugeicons-alert-02 text-n-amber-11'
            : 'i-hugeicons-information-circle text-n-slate-11'
        "
        aria-hidden="true"
      />
      <div class="min-w-0">
        <p
          class="mb-0 text-sm font-semibold"
          :class="isWarning ? 'text-n-amber-12' : 'text-n-slate-12'"
        >
          {{ t(`AI_USAGE_REPORT.BALANCE.STATUS.${statusKey}.TITLE`) }}
        </p>
        <p
          class="mb-0 mt-0.5 text-xs leading-5"
          :class="isWarning ? 'text-n-amber-11' : 'text-n-slate-11'"
        >
          {{ t(`AI_USAGE_REPORT.BALANCE.STATUS.${statusKey}.DESCRIPTION`) }}
        </p>
      </div>
    </div>
  </ReportSectionCard>
</template>
